import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PATH } from 'src/features/navigation/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

import { queryClient } from 'src/lib/react-query';
import { useCopyMembershipType } from 'src/features/membershipType/api/useCopyMembershipType';
import { MembershipType } from 'src/features/membershipType/types';
import { LIST_MEMBERSHIP_TYPES_RESOURCE } from 'src/features/membershipType/api/useListMembershipTypes';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import MembershipTypeFormModal from 'src/features/membershipType/components/forms/MembershipTypeFormModal';
import { useParking } from 'src/providers/ParkingProvider';

interface CopyMembershipTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembershipTemplate?: MembershipType;
    children?: React.ReactNode;
}

function CopyMembershipTypeFormModal<TData>({
    className,
    children,
    selectedMembershipTemplate,
}: CopyMembershipTypeFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const templateName = selectedMembershipTemplate?.translations.find((temp) => temp.language === locale)?.name ?? '';

    const { mutate, isPending } = useCopyMembershipType({
        onSuccess: (newPlan) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPES_RESOURCE] });

            toast({
                title: formatMessage(membershipTypeMessages.copyMembershipTemplateSuccess),
                description: formatMessage(membershipTypeMessages.copyMembershipTemplateSuccessDescription, {
                    entry: templateName,
                }),
            });
            // Land the user on the freshly created plan's dashboard.
            void navigate(`/${PATH.ADMINISTRATION}/membership-templates/${newPlan.id}`);
        },
    });

    return (
        <MembershipTypeFormModal
            selectedMembershipTemplate={selectedMembershipTemplate}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ parkingId, typeId: selectedMembershipTemplate?.id || '', ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.copyEntry, {
                entry: formatMessage(membershipTypeMessages.template).toLowerCase(),
            })}
            icon={ICON_MAP.COPY}
            isPending={isPending}
            subtitle={formatMessage(membershipTypeMessages.copyMembershipTemplateSubtitle, {
                entry: templateName,
            })}
        >
            {children}
        </MembershipTypeFormModal>
    );
}

export default CopyMembershipTypeFormModal;
